import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { ChevronLeft, Play, BookOpen, Star } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (p) => (!p ? null : p.startsWith('http') ? p : `${BACKEND}${p}`);

const CATS = ['All', "Coach's Corner", 'Nutrition', 'Training', 'Recovery', 'Mindset', 'Longevity'];
const CAT_COLOR = {
  "Coach's Corner": '#FF6FA8', Nutrition: '#65A30D', Training: '#00C8FF',
  Recovery: '#7DD3FC', Mindset: '#FFB800', Longevity: '#A78BFA',
};

function ytId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/);
  return m ? m[1] : null;
}

export default function Academy() {
  const nav = useNavigate();
  const [articles, setArticles] = useState([]);
  const [cat, setCat] = useState('All');
  const [active, setActive] = useState(null);

  useEffect(() => { api.get('/articles').then(r => setArticles(r.data)); }, []);

  const filtered = cat === 'All' ? articles : articles.filter(a => a.category === cat);
  const featured = articles.find(a => a.featured) || articles.find(a => a.category === "Coach's Corner");

  if (active) return <ArticleDetail article={active} onBack={() => setActive(null)} />;

  return (
    <div className="space-y-5 pb-8" data-testid="client-academy">
      <div>
        <div className="label-mini text-[#FF6FA8]">Learn</div>
        <h1 className="font-display font-black text-3xl mt-1">YourReGen Academy</h1>
        <p className="text-sm text-muted-foreground mt-1">Learn the YourReGen Protocol across all pillars.</p>
      </div>

      {/* Category chips */}
      <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1 scrollbar-none">
        {CATS.map(c => (
          <button key={c} onClick={() => setCat(c)} data-testid={`academy-cat-${c.replace(/\W/g,'').toLowerCase()}`}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition ${cat === c ? 'bg-white text-[#081726]' : 'bg-[var(--c-film)] text-foreground/80'}`}>
            {c}
          </button>
        ))}
      </div>

      {/* Featured Coach's Corner */}
      {cat === 'All' && featured && (
        <button onClick={() => setActive(featured)} data-testid="academy-featured" className="w-full text-left regen-card-elevated overflow-hidden active:scale-[0.99] transition">
          <div className="relative h-40">
            {imgSrc(featured.image_url) && <img src={imgSrc(featured.image_url)} alt={featured.title} className="absolute inset-0 w-full h-full object-cover" />}
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--c-bg)] via-[var(--c-bg)]/40 to-transparent" />
            <div className="absolute top-3 left-3 pill bg-[#FF6FA8] text-[#081726] text-[10px] font-bold flex items-center gap-1"><Star className="w-3 h-3" /> {featured.category}</div>
            <div className="absolute bottom-3 left-4 right-4">
              <div className="font-display font-black text-lg leading-tight">{featured.title}</div>
              <div className="text-xs text-muted-foreground mt-1 line-clamp-1">{featured.excerpt}</div>
            </div>
          </div>
        </button>
      )}

      {/* Article list */}
      {filtered.length === 0 ? (
        <div className="regen-card-elevated p-8 text-center text-muted-foreground text-sm">
          <BookOpen className="w-7 h-7 mx-auto mb-2 opacity-50" />
          No articles in this category yet.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.filter(a => cat !== 'All' || !a.featured).map(a => {
            const color = CAT_COLOR[a.category] || '#00C8FF';
            return (
              <button key={a.id} onClick={() => setActive(a)} data-testid={`academy-article-${a.id}`} className="w-full text-left regen-card-elevated overflow-hidden flex active:scale-[0.99] transition">
                <div className="w-24 h-24 flex-shrink-0 relative bg-[var(--c-film)]">
                  {imgSrc(a.image_url) && <img src={imgSrc(a.image_url)} alt={a.title} className="absolute inset-0 w-full h-full object-cover" />}
                  {ytId(a.youtube_url) && <div className="absolute inset-0 flex items-center justify-center bg-black/30"><Play className="w-6 h-6 text-[var(--c-ink)]" /></div>}
                </div>
                <div className="flex-1 min-w-0 p-3">
                  <div className="label-mini" style={{ color }}>{a.category}</div>
                  <div className="font-display font-bold text-sm mt-0.5 line-clamp-2">{a.title}</div>
                  <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{a.excerpt}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ArticleDetail({ article, onBack }) {
  const color = CAT_COLOR[article.category] || '#00C8FF';
  const vid = ytId(article.youtube_url);
  return (
    <div className="space-y-4 pb-8" data-testid="academy-detail">
      <button onClick={onBack} data-testid="academy-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground">
        <ChevronLeft className="w-4 h-4" /> Back to YourReGen Academy
      </button>

      {imgSrc(article.image_url) && (
        <div className="rounded-2xl overflow-hidden h-48">
          <img src={imgSrc(article.image_url)} alt={article.title} className="w-full h-full object-cover" />
        </div>
      )}

      <div>
        <div className="label-mini" style={{ color }}>{article.category}</div>
        <h1 className="font-display font-black text-2xl mt-1 leading-tight">{article.title}</h1>
        <div className="text-xs text-muted-foreground mt-1">{article.author} · {article.created_at ? format(new Date(article.created_at), 'MMM d, yyyy') : ''}</div>
      </div>

      {vid && (
        <div className="rounded-2xl overflow-hidden aspect-video">
          <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${vid}`} title={article.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        </div>
      )}

      <div className="text-[15px] leading-relaxed text-foreground/90 space-y-3">
        {article.body.split('\n').filter(l => l.trim() !== '').map((line, i) => (
          <p key={i}>{line}</p>
        ))}
      </div>
    </div>
  );
}
